// @ts-nocheck
import { Grid } from '@mui/material';
import React, {
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef,
} from 'react';
import { FORM_FIELD_DEBOUNCE_MS } from '../../../../hooks/useDebouncedInput';
import PropTypes from 'prop-types';

import FieldInfo from './FieldInfo';
import AttributeEditWithInfo from './AttributeEditWithInfo';
import {
  findGroupNameByGroupId,
  findUsernameByUserId,
  isValueEmpty,
} from '../../../../utils/Utils';
import DerivedAttributeOverrideModal from '../DerivedAttributeOverrideModal';
import { getVisibleAttributes } from '../../../../utils/conditionalRuleEvaluator';
import {
  HDFC_HARDCODED_DEPLOYMENT_WORKFLOW_STATES,
  validateDeploymentDate,
  validateDeploymentDateCommunication,
} from '../../../../utils/deploymentDateUtils';
import { extractAllAttributesFromTemplate } from '../../../../features/configuration/template/components/SectionAndAttribute/commonFunction';
import HistoryDrawer from '../Modals/HistoryDrawer';
import { createInitialFormValues } from '../../../../utils/formUtils';
import { EMPTY_ARRAY, EMPTY_OBJECT, NOOP } from '../../../../const/CommonConst';

const USER_DATA_TYPE = 'User';
const GROUP_DATA_TYPE = 'Group';
const MULTI_SELECT_INPUT_CONTROL = 'MULTI_SELECT';
import AssignUserModal from '../Modals/AssignUserModal';
import {
  SolyticsGrid,
  SolyticsNoDataPresent,
} from '../../../../package/components';

import {
  useGroupsQuery,
  useUsersQuery,
} from '../../../../features/administrativeOptions/userManagement/api/UserManagementQueries';

export const areSetsEqual = (a, b) => {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
};

const formatUserMultiSelectValue = (value, usersList) => {
  if (!Array.isArray(value)) {
    return '';
  }

  return value
    .map((userValue) => findUsernameByUserId(userValue, usersList) || userValue)
    .filter((userName) => !isValueEmpty(userName))
    .join(', ');
};

/**
 * BasicDetails component for displaying basic attributes in a grid layout
 * @param {Object} props - Component props
 * @param {Array} props.attributes - Array of attribute objects to display
 * @param {Object} props.attributeValues - Object containing attribute values keyed by attribute ID
 * @param {Function} props.onEditAttribute - Function to handle edit button click
 * @returns {React.ReactElement} The BasicDetails component
 */
const BasicDetails = ({
  isNewVariant = false,
  attributes,
  attributeValues,
  savedAttributeValues,
  onEditAttribute,
  hasDraftChanges = false,
  mode = 'edit',
  isEditing = false,
  entityDetailsFormikForm,
  isMultipleEditMode,
  teamUsersList = EMPTY_ARRAY,
  isUserAuthorizedForUpdateAttribute,
  allAttributeChats,
  handleOpenChat,
  handleAttributeClick,
  entityData = EMPTY_OBJECT,
  hideHeader,
  isUserAuthorizedToOverrideAttribute,
  overrideData,
  rowNumber = null,
  advancedTableAttributeId = null,
  refreshSingleAttribute,
  handleAutoSave = NOOP,
  getOverrideData,
  getAttributeDetails,
  formValues,
  setFormValues,
  updateInitialValuesFromAPI,
  allSectionList,
  // Constraint-related props
  lookupSourceAttributes = EMPTY_ARRAY,
  autoCompleteSourceAttributes = EMPTY_ARRAY,
  overrideDropdownOptions = EMPTY_OBJECT,
  isLoadingConstraints = false,
  handleAttributeValueChange = NOOP,
  formDetails,
  isHDFCUAMEnabled,
  currentModelStatus,
  dateCalendarConstraints,
  dateCalendarSourceAttributes,
  editableConstraints = EMPTY_OBJECT,
  editableSourceAttributes,
  mandatoryConstraints = EMPTY_OBJECT,
  mandatorySourceAttributes = EMPTY_ARRAY,
  groupDetails = EMPTY_OBJECT,
  groupListForTeamMembers,
  getNextPossibleStates,
  getModelDetails,
  handleGetAttributesData,
  clearDraftValues,
  possibleRoles,
  getCustomTeamDetails,
  // AI Autofill props
  onAcceptAISuggestion,
  onRejectAISuggestion,
  onUndoAISuggestion,
  getSuggestionForAttribute,
  noValidation = false,
  configColumn = false,
  associationTypes = EMPTY_OBJECT,
  modelTemplateDetails = EMPTY_OBJECT,
  maxColumns = 3,
  showAttributeInfo = false,
  attributeSourceMapping = EMPTY_OBJECT,
  isSourceMappingLoading = false,
  getAttributeMapping = NOOP,
}) => {
  const gridItemSize = Math.floor(12 / maxColumns);

  const prevHiddenIdsRef = React.useRef(new Set());
  const [openDerivedModal, setOpenDerivedModal] = useState(false);
  const [isAssignUserModalOpen, setIsAssignUserModalOpen] = useState(false);
  const [selectedAttribute, setSelectedAttribute] = useState(null);
  const [historyAttribute, setHistoryAttribute] = useState(null);
  const isHistoryDrawerOpen = Boolean(historyAttribute);

  const { data: groupList = EMPTY_ARRAY } = useGroupsQuery();
  const { data: usersList = EMPTY_ARRAY } = useUsersQuery();

  const handleChange = useCallback(
    (attribute, value) => {
      const oldValue = entityDetailsFormikForm?.values?.[attribute?.id];

      if (entityDetailsFormikForm) {
        entityDetailsFormikForm?.setFieldValue(attribute?.id, value);
        if (isMultipleEditMode) {
          handleAutoSave(attribute?.id, value, { persist: false });
        }
      }

      // Handle constraint value changes
      if (handleAttributeValueChange) {
        handleAttributeValueChange(attribute?.id, value, oldValue);
      }

      if (
        isHDFCUAMEnabled &&
        HDFC_HARDCODED_DEPLOYMENT_WORKFLOW_STATES.includes(
          currentModelStatus?.currentState
        )
      ) {
        // Get all attributes from template to find the current attribute
        const allAttributesList = extractAllAttributesFromTemplate(formDetails);
        const currentAttribute = allAttributesList?.find(
          (attr) => attr?.id === attribute?.id
        );

        // Validate deployment date (skipped when noValidation=true)
        if (!noValidation) {
          validateDeploymentDate({
            attribute: currentAttribute,
            newValue: value,
            formDetails,
            attributeValues,
          });

          // Validate deployment date communication delay
          validateDeploymentDateCommunication({
            attribute: currentAttribute,
            newValue: value,
            formDetails,
            attributeValues,
          });
        }
      }
    },
    [
      entityDetailsFormikForm,
      isMultipleEditMode,
      handleAutoSave,
      handleAttributeValueChange,
      formDetails,
      attributeValues,
      isHDFCUAMEnabled,
    ]
  );

  // Debounce Formik values for conditional visibility — avoids re-filtering the grid on every keystroke
  const [debouncedFormikValues, setDebouncedFormikValues] = useState(
    entityDetailsFormikForm?.values
  );
  const formikValuesDebounceRef = useRef(null);

  useEffect(() => {
    if (!isMultipleEditMode) {
      setDebouncedFormikValues(entityDetailsFormikForm?.values);
      return undefined;
    }

    if (formikValuesDebounceRef.current) {
      clearTimeout(formikValuesDebounceRef.current);
    }
    formikValuesDebounceRef.current = setTimeout(() => {
      setDebouncedFormikValues(entityDetailsFormikForm?.values);
    }, FORM_FIELD_DEBOUNCE_MS);

    return () => {
      if (formikValuesDebounceRef.current) {
        clearTimeout(formikValuesDebounceRef.current);
      }
    };
  }, [isMultipleEditMode, entityDetailsFormikForm?.values]);

  // Get all current form values for conditional evaluation
  const allFormValues = useMemo(() => {
    if (isMultipleEditMode && debouncedFormikValues) {
      return {
        ...attributeValues,
        ...debouncedFormikValues,
      };
    }
    return attributeValues || {};
  }, [attributeValues, isMultipleEditMode, debouncedFormikValues]);

  // Filter attributes based on conditional visibility rules
  const { visibleAttributes, hiddenAttributes } = useMemo(() => {
    /**
     * on initial render, `allFormValues` will be empty.
     * it is guaranteed that `allFormValues` is never an empty object after initialization,
     * so return early when it is empty.
     */
    if (isValueEmpty(allFormValues)) {
      return {
        // show all attributes if we are not getting allFormValues
        visibleAttributes: attributes,
        hiddenAttributes: [],
      };
    }

    return getVisibleAttributes(attributes, allFormValues);
  }, [attributes, allFormValues]);

  /**
   * we need this useEffect to reset the values of hidden attributes
   * whenever hidden attributes changes and are different from previous hidden attributes
   * we will reset the values of hidden attributes to their initial values
   */
  useEffect(() => {
    /**
     * on initial render we will be getting empty initialValues inside entityDetailsFormikForm
     */
    if (
      !entityDetailsFormikForm?.setFieldValue ||
      !entityDetailsFormikForm?.initialValues ||
      Object.keys(entityDetailsFormikForm?.initialValues).length === 0
    ) {
      return;
    }

    const currentHiddenIds = new Set(hiddenAttributes.map((a) => a.id));
    const prevHiddenIds = prevHiddenIdsRef.current;

    // if hidden sets are equal, do nothing
    // need this to prevent infinite loop
    if (areSetsEqual(prevHiddenIds, currentHiddenIds)) return;

    const newInitialValues = createInitialFormValues(hiddenAttributes);

    // if hidden att set changed -> reset the values of hidden attributes to initial values
    hiddenAttributes.forEach((attr) => {
      const newValue = newInitialValues[attr.id] || null;
      entityDetailsFormikForm.setFieldValue(attr.id, newValue);
    });

    // if code flow reaches here, means hidden sets have changed, update the ref to latest hidden att ids
    prevHiddenIdsRef.current = currentHiddenIds;
  }, [hiddenAttributes, entityDetailsFormikForm]);

  // Memoized function to create onChange handler for specific attribute
  const createAttributeChangeHandler = useCallback(
    (attribute) => (value) => {
      const id = attribute?.id;
      if (setFormValues) {
        setFormValues((prev) => ({
          ...prev,
          [id]: value,
        }));
      }
      if (entityDetailsFormikForm?.setFieldValue) {
        entityDetailsFormikForm.setFieldValue(id, value);
      }
      if (!hideHeader && handleChange) {
        handleChange(attribute, value);
      }
    },
    [handleChange, hideHeader, entityDetailsFormikForm, setFormValues]
  );

  const handleOpenDerivedModal = useCallback(
    (attribute) => {
      setSelectedAttribute(attribute);
      setOpenDerivedModal(true);
    },
    [setSelectedAttribute, setOpenDerivedModal]
  );

  const handleEditClick = useCallback(
    (attribute) => {
      setSelectedAttribute(attribute);
      if (attribute?.name === 'assignedUser' && isHDFCUAMEnabled) {
        setIsAssignUserModalOpen(true);
      } else if (onEditAttribute) {
        onEditAttribute(attribute);
      }
    },
    [
      setSelectedAttribute,
      setIsAssignUserModalOpen,
      onEditAttribute,
      isHDFCUAMEnabled,
    ]
  );

  const handleCloseModal = useCallback(() => {
    setOpenDerivedModal(false);
    setIsAssignUserModalOpen(false);
    setSelectedAttribute(null);
  }, [setOpenDerivedModal, setIsAssignUserModalOpen, setSelectedAttribute]);

  const handleHistoryClick = useCallback(
    (attribute) => {
      setHistoryAttribute(attribute);
    },
    [setHistoryAttribute]
  );

  const handleCloseHistoryDrawer = useCallback(() => {
    setHistoryAttribute(null);
  }, [setHistoryAttribute]);

  // Memoized function to render individual attribute based on mode and editability
  const renderAttribute = useCallback(
    (attribute) => {
      const _attribute = {
        ...(attribute || {}),
        entityType: entityData?.entity_type,
        requestType: entityData?.request_type,
        // Apply editable constraints if present for this attribute
        ...(editableConstraints?.[attribute?.id] !== undefined && {
          is_editable:
            editableConstraints[attribute?.id]?.is_editable !== false,
        }),
        // Apply mandatory constraints if present for this attribute
        ...(mandatoryConstraints?.[attribute?.id] !== undefined &&
          mandatoryConstraints?.[attribute?.id] !== null && {
            is_mandatory:
              mandatoryConstraints[attribute?.id]?.is_mandatory === true,
          }),
      };
      const baseProps = {
        key: attribute?.id,
        label: attribute?.label || attribute?.name,
        value: attributeValues[attribute?.id] || '',
        attribute: _attribute,
        mode,
        isUserAuthorizedForUpdateAttribute,
        entityId: entityData?.entity_id,
        entityType: entityData?.entity_type,
        modelTemplateDetails,
        showAttributeInfo,
        attributeSourceMapping,
        isSourceMappingLoading,
      };
      if (
        attribute?.data_type === USER_DATA_TYPE &&
        attribute?.input_control_type === MULTI_SELECT_INPUT_CONTROL
      ) {
        const userIds = attributeValues[attribute?.id] || [];
        baseProps.value = formatUserMultiSelectValue(userIds, usersList);
      }
      if (
        attribute?.data_type === GROUP_DATA_TYPE &&
        attribute?.input_control_type === MULTI_SELECT_INPUT_CONTROL
      ) {
        const groupIds = attributeValues[attribute?.id] || [];
        const groupNames = groupIds?.map((groupId) =>
          findGroupNameByGroupId(groupId, groupList)
        );
        baseProps.value = groupNames?.join(', ');
      }

      // Create onChange handler for the attribute
      const handleAttributeChange = createAttributeChangeHandler(attribute);

      // Multi-edit but no update permission: read-only (Formik ?? API; then match baseProps multi formatting)
      if (
        isMultipleEditMode &&
        (_attribute?.is_derived || _attribute?.is_editable) &&
        !isUserAuthorizedForUpdateAttribute
      ) {
        let displayVal =
          !hideHeader && entityDetailsFormikForm?.values
            ? (entityDetailsFormikForm.values[attribute?.id] ??
              attributeValues[attribute?.id])
            : baseProps.value;
        displayVal = displayVal ?? '';
        if (
          attribute?.data_type === USER_DATA_TYPE &&
          attribute?.input_control_type === MULTI_SELECT_INPUT_CONTROL
        ) {
          displayVal = formatUserMultiSelectValue(displayVal, usersList);
        } else if (
          attribute?.data_type === GROUP_DATA_TYPE &&
          attribute?.input_control_type === MULTI_SELECT_INPUT_CONTROL
        ) {
          displayVal = (Array.isArray(displayVal) ? displayVal : [])
            .map((id) => findGroupNameByGroupId(id, groupList))
            .join(', ');
        }
        return (
          <Grid
            key={attribute?.id}
            data-attribute-id={attribute?.id}
            size={{
              xs: 12,
              sm: gridItemSize === 12 ? 12 : 6,
              md: gridItemSize,
            }}
            sx={{
              px: isNewVariant ? 0 : 1,
            }}
          >
            <FieldInfo
              {...baseProps}
              value={displayVal}
              isNewVariant={isNewVariant}
              isEditing={isEditing}
              onEdit={handleEditClick}
              hasDraftChanges={hasDraftChanges}
              allAttributeChats={allAttributeChats}
              handleOpenChat={handleOpenChat}
              handleAttributeClick={handleAttributeClick}
              handleOpenDerivedModal={handleOpenDerivedModal}
              overrideData={overrideData}
              entityDetailsFormikForm={entityDetailsFormikForm}
              usersList={usersList}
              groupList={groupList}
              groupDetails={groupDetails}
              onHistoryClick={handleHistoryClick}
              associationTypes={associationTypes}
              entityData={entityData}
            />
          </Grid>
        );
      }

      // Non-edit mode - always show FieldInfo
      if (!isMultipleEditMode) {
        return (
          <Grid
            key={attribute?.id}
            data-attribute-id={attribute?.id}
            size={{
              xs: 12,
              sm: gridItemSize === 12 ? 12 : 6,
              md: gridItemSize,
            }}
          >
            <FieldInfo
              {...baseProps}
              hasDraftChanges={hasDraftChanges}
              onEdit={handleEditClick}
              allAttributeChats={allAttributeChats}
              handleOpenChat={handleOpenChat}
              handleAttributeClick={handleAttributeClick}
              handleOpenDerivedModal={handleOpenDerivedModal}
              overrideData={overrideData}
              entityDetailsFormikForm={entityDetailsFormikForm}
              usersList={usersList}
              groupList={groupList}
              groupDetails={groupDetails}
              onHistoryClick={handleHistoryClick}
              associationTypes={associationTypes}
              isNewVariant={isNewVariant}
              isEditing={isEditing}
              entityData={entityData}
            />
          </Grid>
        );
      }

      // Multiple fields Edit mode — only editable fields (derived only when manageable)
      const canUseMultiEditInlineField =
        _attribute?.is_editable ||
        (_attribute?.is_derived && _attribute?.permission === 'manage');

      if (
        isMultipleEditMode &&
        mode === 'edit' &&
        isUserAuthorizedForUpdateAttribute &&
        canUseMultiEditInlineField
      ) {
        return (
          <Grid
            key={attribute?.id}
            data-attribute-id={attribute?.id}
            size={{
              xs: 12,
              sm: gridItemSize === 12 ? 12 : 6,
              md: gridItemSize,
            }}
            sx={{
              px: isNewVariant ? 0 : 1,
            }}
          >
            <AttributeEditWithInfo
              attribute={_attribute}
              showAttributeInfo={showAttributeInfo}
              attributeSourceMapping={attributeSourceMapping}
              isSourceMappingLoading={isSourceMappingLoading}
              isNewVariant={isNewVariant}
              fieldProps={{
                value: hideHeader
                  ? (formValues[attribute?.id] ?? '')
                  : (entityDetailsFormikForm?.values?.[attribute?.id] ?? ''),
                onChange: handleAttributeChange,
                isUserAuthorizedForUpdateAttribute,
                isMultipleEditMode,
                teamUsersList,
                entityDetailsFormikForm,
                entityData,
                handleOpenDerivedModal,
                handleAutoSave,
                hideHeader,
                isUserAuthorizedToOverrideAttribute,
                lookupSourceAttributes,
                autoCompleteSourceAttributes,
                overrideDropdownOptions,
                isLoadingConstraints,
                dateCalendarConstraints,
                dateCalendarSourceAttributes,
                editableSourceAttributes,
                mandatorySourceAttributes,
                groupDetails,
                attributeValues,
                savedAttributeValues,
                aiSuggestion: getSuggestionForAttribute?.(attribute?.id),
                onAcceptAISuggestion,
                onRejectAISuggestion,
                onUndoAISuggestion,
                configColumn,
                modelTemplateDetails,
                onHistoryClick: handleHistoryClick,
              }}
            />
          </Grid>
        );
      }

      // Edit mode but attribute is not editable (or section permission is
      // view-only) - show FieldInfo
      if (isMultipleEditMode && (mode !== 'edit' || !_attribute?.is_editable)) {
        return (
          <Grid
            key={attribute?.id}
            data-attribute-id={attribute?.id}
            size={{
              xs: 12,
              sm: gridItemSize === 12 ? 12 : 6,
              md: gridItemSize,
            }}
            sx={{
              px: isNewVariant ? 0 : 1,
            }}
          >
            <FieldInfo
              {...baseProps}
              onEdit={handleEditClick}
              hasDraftChanges={hasDraftChanges}
              allAttributeChats={allAttributeChats}
              handleOpenChat={handleOpenChat}
              handleAttributeClick={handleAttributeClick}
              overrideData={overrideData}
              groupDetails={groupDetails}
              entityDetailsFormikForm={entityDetailsFormikForm}
              usersList={usersList}
              groupList={groupList}
              onHistoryClick={handleHistoryClick}
              associationTypes={associationTypes}
              isNewVariant={isNewVariant}
              isEditing={isEditing}
              entityData={entityData}
            />
          </Grid>
        );
      }
    },
    [
      attributeValues,
      savedAttributeValues,
      mode,
      isEditing,
      isMultipleEditMode,
      isNewVariant,
      handleEditClick,
      entityDetailsFormikForm,
      allAttributeChats,
      handleOpenChat,
      handleAttributeClick,
      usersList,
      teamUsersList,
      groupList,
      createAttributeChangeHandler,
      overrideData,
      handleOpenDerivedModal,
      formValues,
      entityData,
      editableConstraints,
      hasDraftChanges,
      hideHeader,
      lookupSourceAttributes,
      autoCompleteSourceAttributes,
      overrideDropdownOptions,
      isLoadingConstraints,
      dateCalendarConstraints,
      dateCalendarSourceAttributes,
      editableSourceAttributes,
      groupDetails,
      handleAutoSave,
      isUserAuthorizedForUpdateAttribute,
      handleHistoryClick,
      associationTypes,
      modelTemplateDetails,
      gridItemSize,
      showAttributeInfo,
      attributeSourceMapping,
      isSourceMappingLoading,
    ]
  );

  // Memoized rendered attributes - now using visible attributes
  const renderedAttributes = useMemo(() => {
    if (!isNewVariant) {
      return visibleAttributes.map((attribute) => renderAttribute(attribute));
    }

    //new variant
    if (isValueEmpty(visibleAttributes)) {
      return (
        <SolyticsNoDataPresent
          variant="iconWithText"
          message="No attributes to display"
        />
      );
    }

    return (
      <SolyticsGrid
        container
        size={12}
        columnSpacing={2}
        sx={{
          rowGap: 2,
          maxHeight: 'fit-content',
          width: '100%',
          '& > .MuiGrid-item': { minWidth: 0 },
        }}
      >
        {visibleAttributes.map((attribute) => renderAttribute(attribute))}
      </SolyticsGrid>
    );
  }, [visibleAttributes, renderAttribute]);

  const handleCloseDerivedModal = useCallback(() => {
    setOpenDerivedModal(false);
  }, [setOpenDerivedModal]);

  const handleAssignUserSave = useCallback(() => {
    updateInitialValuesFromAPI();
  }, [updateInitialValuesFromAPI]);

  return (
    <>
      <Grid
        container
        className="summary-card"
        size={12}
        sx={{
          rowGap: 2,
          p: isNewVariant ? 0 : 2.5,
        }}
      >
        {renderedAttributes}
      </Grid>
      {openDerivedModal && (
        <DerivedAttributeOverrideModal
          open={openDerivedModal}
          handleClose={handleCloseDerivedModal}
          attribute={selectedAttribute}
          entityDetailsFormikForm={entityDetailsFormikForm}
          teamUsersList={teamUsersList}
          allAttributeChats={allAttributeChats}
          entityData={entityData}
          isUserAuthorizedToOverrideAttribute={
            isUserAuthorizedToOverrideAttribute
          }
          overrideData={overrideData}
          rowNumber={rowNumber}
          advancedTableAttributeId={advancedTableAttributeId}
          refreshSingleAttribute={refreshSingleAttribute}
          getOverrideData={getOverrideData}
          getAttributeDetails={getAttributeDetails}
          updateInitialValuesFromAPI={updateInitialValuesFromAPI}
          handleAutoSave={handleAutoSave}
          allSectionList={allSectionList}
          handleAttributeValueChange={handleAttributeValueChange}
          getAttributeMapping={getAttributeMapping}
        />
      )}
      {isAssignUserModalOpen && (
        <AssignUserModal
          open={isAssignUserModalOpen}
          handleClose={handleCloseModal}
          attribute={selectedAttribute}
          entityData={entityData}
          usersList={usersList}
          teamUsersList={teamUsersList}
          groupList={groupListForTeamMembers}
          value={attributeValues[selectedAttribute?.id] || ''}
          onSave={handleAssignUserSave}
          possibleRoles={possibleRoles}
          getNextPossibleStates={getNextPossibleStates}
          getModelDetails={getModelDetails}
          handleGetAttributesData={handleGetAttributesData}
          clearDraftValues={clearDraftValues}
          getCustomTeamDetails={getCustomTeamDetails}
        />
      )}
      {isHistoryDrawerOpen && (
        <HistoryDrawer
          open={isHistoryDrawerOpen}
          handleClose={handleCloseHistoryDrawer}
          attribute={historyAttribute}
          entityId={entityData?.entity_id}
          entityType={entityData?.entity_type}
        />
      )}
    </>
  );
};

BasicDetails.propTypes = {
  isNewVariant: PropTypes.bool,
  attributes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      label: PropTypes.string,
      is_editable: PropTypes.bool,
    })
  ).isRequired,
  attributeValues: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
      PropTypes.object,
    ])
  ),
  savedAttributeValues: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
      PropTypes.object,
    ])
  ),
  onEditAttribute: PropTypes.func,
  hasDraftChanges: PropTypes.bool,
  mode: PropTypes.oneOf(['edit', 'view']),
  isEditing: PropTypes.bool,
  entityDetailsFormikForm: PropTypes.shape({
    values: PropTypes.objectOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
        PropTypes.array,
        PropTypes.object,
      ])
    ),
    setFieldValue: PropTypes.func,
    initialValues: PropTypes.shape({}),
  }),
  isMultipleEditMode: PropTypes.bool,
  teamUsersList: PropTypes.arrayOf(PropTypes.shape({})),
  isUserAuthorizedForUpdateAttribute: PropTypes.bool.isRequired,
  allAttributeChats: PropTypes.arrayOf(PropTypes.shape({})),
  handleOpenChat: PropTypes.func.isRequired,
  handleAttributeClick: PropTypes.func.isRequired,
  entityData: PropTypes.shape({
    entity_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    entity_type: PropTypes.string,
  }),
  hideHeader: PropTypes.bool,
  isUserAuthorizedToOverrideAttribute: PropTypes.bool.isRequired,
  overrideData: PropTypes.oneOf([PropTypes.object]).isRequired,
  rowNumber: PropTypes.number,
  advancedTableAttributeId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  refreshSingleAttribute: PropTypes.func.isRequired,
  handleAutoSave: PropTypes.func,
  getOverrideData: PropTypes.func.isRequired,
  getAttributeDetails: PropTypes.func.isRequired,
  formValues: PropTypes.oneOf([PropTypes.object]).isRequired,
  setFormValues: PropTypes.func.isRequired,
  updateInitialValuesFromAPI: PropTypes.func.isRequired,
  allSectionList: PropTypes.oneOf([PropTypes.object]).isRequired,
  // Constraint-related props
  lookupSourceAttributes: PropTypes.arrayOf(PropTypes.shape({})),
  autoCompleteSourceAttributes: PropTypes.arrayOf(PropTypes.shape({})),
  dateCalendarSourceAttributes: PropTypes.arrayOf(PropTypes.shape({})),
  dateCalendarConstraints: PropTypes.shape({}),
  editableConstraints: PropTypes.shape({}),
  editableSourceAttributes: PropTypes.arrayOf(PropTypes.shape({})),
  overrideDropdownOptions: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.object])
  ),
  mandatoryConstraints: PropTypes.shape({}),
  mandatorySourceAttributes: PropTypes.arrayOf(PropTypes.number),
  isLoadingConstraints: PropTypes.bool,
  handleAttributeValueChange: PropTypes.func,
  formDetails: PropTypes.shape({}),
  isHDFCUAMEnabled: PropTypes.bool,
  currentModelStatus: PropTypes.shape({
    currentState: PropTypes.string,
  }),
  groupDetails: PropTypes.shape({
    isGroupAdmin: PropTypes.bool,
    userName: PropTypes.string,
    userId: PropTypes.number,
    users: PropTypes.arrayOf(
      PropTypes.shape({
        userId: PropTypes.number,
        username: PropTypes.string,
      })
    ),
  }),
  groupListForTeamMembers: PropTypes.arrayOf(PropTypes.shape({})),
  getNextPossibleStates: PropTypes.func,
  getModelDetails: PropTypes.func,
  handleGetAttributesData: PropTypes.func,
  clearDraftValues: PropTypes.func,
  possibleRoles: PropTypes.arrayOf(PropTypes.string),
  getCustomTeamDetails: PropTypes.func,
  // AI Autofill
  onAcceptAISuggestion: PropTypes.func,
  onRejectAISuggestion: PropTypes.func,
  onUndoAISuggestion: PropTypes.func,
  getSuggestionForAttribute: PropTypes.func,
  noValidation: PropTypes.bool,
  configColumn: PropTypes.bool,
  associationTypes: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        entity_name: PropTypes.string,
        entity_type: PropTypes.string,
        template_id: PropTypes.string,
        version_id: PropTypes.string,
      })
    )
  ),
  modelTemplateDetails: PropTypes.shape({}),
  maxColumns: PropTypes.number,
  showAttributeInfo: PropTypes.bool,
  attributeSourceMapping: PropTypes.objectOf(PropTypes.shape({})),
  isSourceMappingLoading: PropTypes.bool,
  getAttributeMapping: PropTypes.func,
};

export default BasicDetails;
